// src/contexts/AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { getAuthToken, getUsername, getUserRole, getUserProfile, signOut, deriveCustomerNameFromEmail } from "../utils/auth";
import { USERS_URL } from "../config/apiConfig";
import { connectOrbieeChatSocket } from "../services/websocket";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const fetchAdminUsers = async () => {
  const response = await fetch(USERS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to fetch users");
  return data.users;
};

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    loading: true,
  });

  // On mount: restore auth state from cookies (returning / page-refresh user).
  // Does NOT dispatch authStateChanged — this is not a new login event.
  useEffect(() => {
    const initializeAuth = async () => {
      const token    = getAuthToken();
      const username = getUsername();
      const role     = getUserRole();

      if (token && username) {
        if (role === "C") {
          const profile = getUserProfile();
          setAuthState({
            user: profile
              ? { ...profile, role: profile.role || role }
              : {
                  username,
                  role,
                  customer_name: deriveCustomerNameFromEmail(username),
                },
            loading: false,
          });
          if (profile?.customer_id) {
            connectOrbieeChatSocket(profile.customer_id).catch(console.warn);
          }
        } else if (role === "A") {
          try {
            const users = await fetchAdminUsers();
            setAuthState({ user: { username, role, users }, loading: false });
          } catch (error) {
            console.error("Failed to load admin data:", error);
            signOut();
            setAuthState({ user: null, loading: false });
          }
        }
      } else {
        setAuthState({ user: null, loading: false });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Called by SignInModal after a successful Firebase login + validateUser.
   * Accepts the profile object already built by the caller.
   *
   * Sequence (in order):
   *  1. Update React auth state — all useAuth() consumers re-render immediately.
   *  2. Await WebSocket open (or timeout) — avatar trigger is deferred until WS is stable.
   *  3. Dispatch "authStateChanged" — ChatBot picks this up and plays the greeting.
   */
  const login = async (profile) => {
    // 1. React state — components that use useAuth() see the user immediately.
    setAuthState({ user: profile, loading: false });

    // 2. WebSocket — await open so avatar starts only after the connection is stable.
    if (profile?.customer_id) {
      await connectOrbieeChatSocket(profile.customer_id);
    }

    // 3. Notify the app — fired only after WS is confirmed open (or timed out).
    window.dispatchEvent(new CustomEvent("authStateChanged", { detail: profile }));
  };

  const logout = () => {
    signOut();
    setAuthState({ user: null, loading: false });
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

